// One-glance aggregate panel that sits above the per-event Delivery
// Events Timeline. Pulls everything from the events array we already
// load — no extra queries, no backend changes.
//
// Layout:
//   Funnel:    Fanned out → Delivered → Clicked     | sidebar: Rejected, Dismissed, No targets
//   Rates:                  reach %     engagement %
//   Headline:  one-line summary picked from the data
//   Rejections: top FCM error codes with counts (only when fcm_rejected > 0)

const STATUS_STYLES = {
  fanned_out: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
  delivered: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
  clicked: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200',
  closed: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  fcm_rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  no_targets: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  permission_revoked: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
}

const STATUS_LABELS = {
  fanned_out: 'Fanned out',
  delivered: 'Delivered',
  clicked: 'Clicked',
  closed: 'Dismissed',
  fcm_rejected: 'Rejected',
  no_targets: 'No targets',
  failed: 'Failed',
  permission_revoked: 'Perm revoked',
}

// Plain-English explanations surfaced as tooltips on the funnel pills.
// Keep them short — these are hover hints, not full docs.
const STATUS_TOOLTIPS = {
  fanned_out:
    'Devices the FCM API accepted for delivery. Includes everyone reachable, not just those who actually saw the notification.',
  delivered:
    "Devices that received the push and the service worker reported back. The 'reached real screens' count.",
  clicked:
    'Devices where the user clicked the notification (or focused an existing tab via the click handler).',
  closed:
    'Devices where the user dismissed the notification without clicking.',
  fcm_rejected:
    'Devices the FCM API refused. Most are stale tokens (UNREGISTERED) — see the rejection code breakdown below.',
  no_targets:
    'The audience tag had no registered devices at send time. The campaign created an entry but never reached anyone.',
  failed:
    'The send pipeline itself failed before reaching FCM (rare — usually a config issue).',
  permission_revoked:
    'Notification permission was revoked on the device. Future sends to this device will be silently dropped until permission is regranted.',
}

// Operational hints shown on hover for FCM error codes. Mirrors the codeMap
// in PushEventMeta.jsx so per-event and aggregate views agree.
const FCM_CODE_TOOLTIPS = {
  UNREGISTERED:
    'Token expired or unregistered. Safe to retire this device — it will keep failing on every send until cleaned up.',
  INVALID_ARGUMENT:
    'Malformed request payload. Caller-side bug, not a per-device problem.',
  SENDER_ID_MISMATCH:
    'Device subscribed to FCM under a different sender ID than Push-Send is using. Indicates a Firebase project or VAPID-key mismatch — alert ops, this will not auto-resolve.',
  QUOTA_EXCEEDED: 'FCM rate limit hit. Back off and retry later.',
  UNAVAILABLE: 'FCM transient issue. Retry should succeed.',
  INTERNAL: 'FCM internal error. Retry should succeed.',
  THIRD_PARTY_AUTH_ERROR: 'APNS or web push auth failure.',
  TRANSPORT_ERROR: 'Network failure reaching FCM (transient).',
}

function tally(events) {
  const counts = {}
  for (const ev of events ?? []) {
    const s = String(ev?.Status || '').toLowerCase()
    if (!s) continue
    counts[s] = (counts[s] || 0) + 1
  }
  return counts
}

function topRejectionCodes(events) {
  const codes = {}
  for (const ev of events ?? []) {
    if (String(ev?.Status || '').toLowerCase() !== 'fcm_rejected') continue
    const code = ev?.Meta?.fcm_error_code || ev?.Meta?.fcm_status || 'UNKNOWN'
    codes[code] = (codes[code] || 0) + 1
  }
  return Object.entries(codes)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
}

function pct(numerator, denominator) {
  if (!denominator || denominator <= 0) return null
  if (numerator <= 0) return 0
  return Math.round((numerator / denominator) * 100)
}

function pickHeadline(c) {
  // Plain-English summary tied to whichever signal tells the strongest
  // story for this entry. Order matters — the first match wins.
  const fanned = c.fanned_out || 0
  const delivered = c.delivered || 0
  const clicked = c.clicked || 0
  const rejected = c.fcm_rejected || 0
  const dismissed = c.closed || 0
  const noTargets = c.no_targets || 0

  if (noTargets > 0 && fanned === 0) {
    return 'No devices matched this audience tag'
  }
  if (rejected > 0 && fanned === 0) {
    return `All ${rejected} device${rejected === 1 ? '' : 's'} rejected by FCM — likely stale tokens`
  }
  if (clicked > 0) {
    if (clicked === delivered) {
      return delivered === 1
        ? 'Delivered to 1 device, and they clicked'
        : `All ${delivered} delivered devices clicked`
    }
    return `${clicked} of ${delivered} delivered device${delivered === 1 ? '' : 's'} clicked`
  }
  if (delivered > 0 && clicked === 0) {
    if (dismissed > 0) {
      return `Delivered to ${delivered} device${delivered === 1 ? '' : 's'}, ${dismissed} dismissed without clicking`
    }
    return delivered === 1
      ? '1 device received the notification but hasn’t clicked yet'
      : `${delivered} devices received the notification but haven’t clicked yet`
  }
  if (fanned > 0 && delivered === 0) {
    return `Sent to ${fanned} device${fanned === 1 ? '' : 's'}, none have reported back yet`
  }
  return 'Awaiting first device callback…'
}

function FunnelPill({ status, count, suffix }) {
  return (
    <span
      title={STATUS_TOOLTIPS[status]}
      className={`inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded text-xs font-medium cursor-help ${
        STATUS_STYLES[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
      }`}
    >
      <span className="font-semibold tabular-nums">{count}</span>
      <span className="opacity-80">{STATUS_LABELS[status] || status}</span>
      {suffix && <span className="opacity-60 font-normal">{suffix}</span>}
    </span>
  )
}

function RateBadge({ value, label, tooltip }) {
  return (
    <span
      title={tooltip}
      className="inline-flex flex-col items-center text-center px-1 leading-tight cursor-help"
    >
      <span className="text-[11px] font-semibold tabular-nums text-gray-700 dark:text-gray-200">
        {value}%
      </span>
      <span className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
    </span>
  )
}

function Arrow() {
  return (
    <span className="text-gray-400 dark:text-gray-600 select-none" aria-hidden="true">
      &rarr;
    </span>
  )
}

export default function PushEntryAggregate({ events }) {
  const c = tally(events)
  const fanned = c.fanned_out || 0
  const delivered = c.delivered || 0
  const clicked = c.clicked || 0
  const rejected = c.fcm_rejected || 0
  const dismissed = c.closed || 0
  const noTargets = c.no_targets || 0
  const failed = c.failed || 0
  const permRevoked = c.permission_revoked || 0

  const reachRate = pct(delivered, fanned)
  // Engagement = clicked / delivered. Frame: of those who received the
  // notification, how many clicked. (User confirmed this denominator.)
  const engagementRate = pct(clicked, delivered)

  const headline = pickHeadline(c)
  const rejections = topRejectionCodes(events)
  const sideline = [
    rejected > 0 && { status: 'fcm_rejected', count: rejected },
    dismissed > 0 && { status: 'closed', count: dismissed },
    noTargets > 0 && { status: 'no_targets', count: noTargets },
    failed > 0 && { status: 'failed', count: failed },
    permRevoked > 0 && { status: 'permission_revoked', count: permRevoked },
  ].filter(Boolean)

  // Funnel always renders even with zeros — the gap between fanned_out
  // and delivered is itself useful signal ("0 of 13 delivered").
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {headline}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <FunnelPill status="fanned_out" count={fanned} />
        {reachRate !== null && (
          <>
            <Arrow />
            <RateBadge
              value={reachRate}
              label="reach"
              tooltip="Reach = delivered / fanned out. The share of FCM-accepted devices that successfully rendered the notification and reported back."
            />
            <Arrow />
          </>
        )}
        <FunnelPill status="delivered" count={delivered} />
        {engagementRate !== null && (
          <>
            <Arrow />
            <RateBadge
              value={engagementRate}
              label="CTR"
              tooltip="Clicked / delivered. Of devices that received the notification, the share that clicked it."
            />
            <Arrow />
          </>
        )}
        <FunnelPill status="clicked" count={clicked} />

        {sideline.length > 0 && (
          <>
            <span className="text-gray-300 dark:text-gray-700 px-1 select-none" aria-hidden="true">
              |
            </span>
            {sideline.map(({ status, count }) => (
              <FunnelPill key={status} status={status} count={count} />
            ))}
          </>
        )}
      </div>

      {rejections.length > 0 && (
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <span
            className="font-medium text-gray-700 dark:text-gray-300 cursor-help"
            title="Most common reasons FCM refused to deliver. Hover each code for the operational meaning."
          >
            Top rejection codes:
          </span>{' '}
          {rejections.map(([code, n], i) => (
            <span key={code}>
              {i > 0 && <span className="text-gray-400">, </span>}
              <span
                className="font-mono cursor-help"
                title={FCM_CODE_TOOLTIPS[code] || `Less common FCM rejection: ${code}`}
              >
                {code}
              </span>
              <span className="text-gray-500"> ({n})</span>
            </span>
          ))}
          {rejections.some(([code]) => code === 'UNREGISTERED') && (
            <span className="block mt-1 text-[11px] text-gray-500 dark:text-gray-500">
              UNREGISTERED tokens are safe to retire — they will keep failing on every send until cleaned up.
            </span>
          )}
        </div>
      )}
    </div>
  )
}
